import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import type { UpdatePersonaDto } from '@inference.engine/persona-api-client';

import type { PersonaDto } from '@/lib/api/personas-client';
import { usePermissions } from '@/entities/user';
import {
  PERSONA_QUERY_KEYS,
  personaWorkspacesQuery,
  updatePersonaWorkspaces as updateScope,
} from '@/entities/persona';
import { notificationService } from '@/lib/services/notification.service';
import { diffWorkspaceScope } from '@/shared/lib/workspace-scope';

import {
  type EditPersonaFormData,
  FIELD_META,
  FORM_TABS,
  FORM_TAB_ARRAY,
  buildEditPersonaFormSchema,
  mapFormDataToUpdatePayload,
  mapPersonaDtoToFormData,
  toScopeVisibility,
} from '../../EditPersonaForm.schema';

/** Notifications from this form sit bottom-centre, away from the modal's own actions. */
const NOTIFICATION_POSITION: Pick<
  NonNullable<Parameters<typeof notificationService.showError>[1]>,
  'anchorOrigin'
> = {
  anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
};

/**
 * Cross-feature deps injected into `useEditPersonaForm` (Springboard
 * dependency injection). Consumer (Audiences page → AudiencesContent → EditPersonaForm)
 * obtains the persona-update mutation from `@/features/persona-sparks` and
 * threads its async caller down. Signature mirrors the public API of
 * `useUpdatePersona().mutateAsync` from persona-sparks. Defined here
 * (instead of imported) to avoid cross-feature OUT.
 */
interface UpdatePersonaDeps {
  updatePersona: (params: { id: string; payload: UpdatePersonaDto }) => Promise<unknown>;
}

export interface UseEditPersonaFormOptions extends UpdatePersonaDeps {
  persona: PersonaDto;
  onSuccess?: () => void;
  onClose?: () => void;
}

export type UseEditPersonaFormReturn = ReturnType<typeof useEditPersonaForm>;

/**
 * Main hook for the edit persona form.
 * Uses React Hook Form with Zod validation and tab-based navigation.
 */
export function useEditPersonaForm(options: UseEditPersonaFormOptions) {
  const { t } = useTranslation('audiences');
  const { t: tValidation } = useTranslation('validation');

  const { persona, onSuccess, onClose, updatePersona } = options;
  const queryClient = useQueryClient();

  const gwiLinkingEnabled = useBooleanFlagValue('gwi_audience_linking', false);
  const { canAccessGwiIntegration } = usePermissions();
  const canEditGwiLink = gwiLinkingEnabled && canAccessGwiIntegration;

  const canSelectWorkspaces = useBooleanFlagValue('persona_workspace_selection', false);

  // The granted workspaces are not on the persona row, so the tab loads them separately. Until
  // this resolves the tab shows the coarse org-wide/workspace split `mapPersonaDtoToFormData`
  // derives from `visibility`.
  const { data: currentScope } = useQuery({
    ...personaWorkspacesQuery(persona.id),
    enabled: canSelectWorkspaces && persona.type === 'Scoped',
  });

  const defaultValues = useMemo(() => {
    const data = mapPersonaDtoToFormData(persona);
    // Keep the field out of form state entirely when the user can't edit it,
    // so submits don't echo it back and trip the backend's GWI permission guard.
    if (!canEditGwiLink) delete data.gwiId;
    return data;
  }, [persona, canEditGwiLink]);

  const formSchema = useMemo(() => buildEditPersonaFormSchema(tValidation), [tValidation]);

  const form = useForm<EditPersonaFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onTouched',
  });

  // Seeds the picker once the real scope lands, without touching fields the user may already be
  // editing. `keepDirtyValues` is what protects an in-flight edit from the late response.
  const seededScopeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentScope || seededScopeRef.current === persona.id) return;
    seededScopeRef.current = persona.id;

    form.reset(
      {
        ...form.getValues(),
        spaceVisibility: currentScope.visibility,
        workspaceIds: currentScope.workspaceIds,
      },
      { keepDirtyValues: true },
    );
  }, [currentScope, form, persona.id]);

  const [activeTabValue, setActiveTabValue] = useState<number>(FORM_TABS.personaInformation.value);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [isGeneratingMedia, setIsGeneratingMedia] = useState(false);

  // CRITICAL: isProcessingMedia gates ALL action buttons, tab navigation, and save button.
  // isUploadingMedia = S3 upload in progress
  // isGeneratingMedia = AI generation/polling in progress (connected in TICKET-3/TICKET-4)
  const isProcessingMedia = isUploadingMedia || isGeneratingMedia;

  const activeTab =
    FORM_TAB_ARRAY.find((t) => t.value === activeTabValue) ?? FORM_TABS.personaInformation;

  const biographyItemsArray = useFieldArray({
    control: form.control,
    name: 'biographyItems',
  });

  const caresAboutArray = useFieldArray({
    control: form.control,
    name: 'caresAbout',
  });

  const painsArray = useFieldArray({
    control: form.control,
    name: 'pains',
  });

  const gainsArray = useFieldArray({
    control: form.control,
    name: 'gains',
  });

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const confirmIfDirty = useCallback(
    (action: () => void) => {
      if (form.formState.isDirty) {
        pendingActionRef.current = action;
        setShowUnsavedModal(true);
        return;
      }
      action();
    },
    [form.formState.isDirty],
  );

  const handleConfirmLeave = useCallback(() => {
    setShowUnsavedModal(false);
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  }, []);

  const handleCancelLeave = useCallback(() => {
    setShowUnsavedModal(false);
    pendingActionRef.current = null;
  }, []);

  // Tabs are views over a single form, so navigating between them must preserve
  // every field's value (including generated media on the Profile media tab).
  // Resetting here previously discarded cross-tab edits, trapping the user when
  // a validation error lived on another tab. The unsaved-changes guard stays on
  // handleClose, where discarding is the intended outcome.
  const handleTabChange = useCallback(
    (value: number) => {
      if (value === activeTabValue) return;

      setActiveTabValue(value);
    },
    [activeTabValue],
  );

  const handleClose = useCallback(() => {
    confirmIfDirty(() => onClose?.());
  }, [confirmIfDirty, onClose]);

  /**
   * Scope travels on its own endpoint, not in the persona payload. `add` and `remove` are the
   * difference against what loaded, which is what leaves a workspace this caller cannot see in
   * neither list.
   */
  const saveScopeIfChanged = useCallback(
    async (formData: EditPersonaFormData) => {
      if (!canSelectWorkspaces || !currentScope) return;

      const delta = diffWorkspaceScope(
        currentScope,
        {
          visibility: toScopeVisibility(formData.spaceVisibility),
          workspaceIds: formData.workspaceIds,
        },
        'organization',
      );
      if (!delta) return;

      const updated = await updateScope(persona.id, delta);

      // The PATCH answers with the resulting scope, so invalidating would only refetch what is
      // already in hand — and that GET costs a permission lookup per granted workspace.
      queryClient.setQueryData(PERSONA_QUERY_KEYS.workspaces(persona.id), updated);
    },
    [canSelectWorkspaces, currentScope, persona.id, queryClient],
  );

  const onSubmit = useCallback(
    async (formData: EditPersonaFormData) => {
      const payload = mapFormDataToUpdatePayload(formData, persona);

      try {
        await updatePersona({
          id: persona.id,
          payload,
        });
      } catch {
        notificationService.showError(t('audiences.useEditPersonaForm.failedToUpdatePersona'), {
          ...NOTIFICATION_POSITION,
          description: t('audiences.useEditPersonaForm.tryAgainAndIfTheError'),
        });
        return;
      }

      // Scope travels on its own endpoint, so it fails on its own terms — the empty-set reject,
      // or the 403 a non-admin gets for changing visibility. Reporting that as "failed to update
      // persona" would send the user back to re-save content that already landed, so the message
      // names what actually failed and the form stays open on the tab that owns it.
      try {
        await saveScopeIfChanged(formData);
      } catch {
        setActiveTabValue(FORM_TABS.spaceVisibility.value);
        notificationService.showError(t('audiences.useEditPersonaForm.personaSavedButSharingWas'), {
          ...NOTIFICATION_POSITION,
          description: t('audiences.useEditPersonaForm.yourChangesToWhoCan'),
        });
        return;
      }

      notificationService.showSuccess(
        t('audiences.useEditPersonaForm.personaSuccessfullyUpdated'),
        NOTIFICATION_POSITION,
      );
      onSuccess?.();
    },
    [updatePersona, persona, onSuccess, saveScopeIfChanged, t],
  );

  const submitForm = useCallback(async () => {
    const isValid = await form.trigger();

    if (!isValid) {
      // Collect failing top-level field names. RHF's formState.errors can be
      // empty when the Zod resolver rejects a field with no touched/registered
      // input, so fall back to parsing the schema directly to recover the paths.
      let failingFields = Object.keys(form.formState.errors);
      if (failingFields.length === 0) {
        const parsed = formSchema.safeParse(form.getValues());
        if (!parsed.success) {
          failingFields = [...new Set(parsed.error.issues.map((i) => String(i.path[0])))];
        }
      }

      // failingFields are runtime strings (nested Zod paths may not be top-level
      // form keys), so look them up through a partial view of FIELD_META.
      const fieldMeta = FIELD_META as Partial<Record<string, { label: string; tab: number }>>;
      const meta = failingFields.reduce<{ label: string; tab: number }[]>((acc, f) => {
        const entry = fieldMeta[f];
        if (entry) acc.push(entry);
        return acc;
      }, []);

      const labels = meta.map((m) => m.label);

      // Jump to the tab holding the first failing field so the user can see it.
      // Setting state to the current tab is a no-op in React, so no guard needed.
      const firstMeta = meta.find(Boolean);
      if (firstMeta) {
        setActiveTabValue(firstMeta.tab);
      }

      notificationService.showError(
        labels.length > 0
          ? t('audiences.useEditPersonaForm.pleaseFixTheFollowing', {
              fields: labels.join(', '),
            })
          : t('audiences.useEditPersonaForm.pleaseFixTheErrorsInThe'),
      );
      return;
    }

    await form.handleSubmit(onSubmit)();
  }, [form, formSchema, onSubmit, t]);

  return {
    ...form,
    // Persona identity
    personaId: persona.id,
    // Tab navigation
    activeTabValue,
    activeTab,
    handleTabChange,
    // Dynamic arrays
    biographyItemsArray,
    caresAboutArray,
    painsArray,
    gainsArray,
    // Actions
    handleClose,
    submitForm,
    // Unsaved changes modal
    showUnsavedModal,
    handleConfirmLeave,
    handleCancelLeave,
    // Media upload state
    isUploadingMedia,
    setIsUploadingMedia,
    isGeneratingMedia,
    setIsGeneratingMedia,
    isProcessingMedia,
    /** A global persona belongs to no organization, so it has no workspaces to be shared with. */
    canEditSpaceVisibility: canSelectWorkspaces && persona.type === 'Scoped',
    /**
     * With the picker available the edit form names workspaces outright, so the create-time
     * "this workspace" shorthand is not offered: it maps onto no reachable delta.
     */
    includeCurrentWorkspaceOption: !canSelectWorkspaces,
    // State
    isDirty: form.formState.isDirty,
    isSubmitting: form.formState.isSubmitting,
  };
}
